import * as R from 'ramda';
import * as P from 'plow-js';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
// feature fleet-profile
import * as A from '../actions';
//////////////////////////////////////////////////

// safety violation
const getSafetyViolationListByEntityTypeSuccess = (state, { data, entityType, primaryObjectGuid }) => {
  const list = R.compose(
    R.indexBy(R.prop(GC.FIELD_GUID)),
    G.mapIndexed((item, index) => R.mergeRight(item, { index, expanded: false })),
  )(R.or(data, []));

  if (R.equals(entityType, 'safetyViolation')) return P.$set('driver.safetyViolation', list, state);

  return P.$set(`driver.safetyViolation.${primaryObjectGuid}.${entityType}s`, list, state);
};

const toggleSafetyViolationItem = (state, { guid, componentType = null }) => {
  const item = R.pathOr({}, ['driver', 'safetyViolation', guid], state);

  if (G.isAllTrue(
    item.expanded,
    G.notEquals(componentType, item.componentType),
    R.includes(componentType, ['note', 'document']),
  )) return P.$set(`driver.safetyViolation.${guid}.componentType`, componentType, state);

  return (
    P.$all(
      P.$toggle(`driver.safetyViolation.${guid}.expanded`),
      P.$set(`driver.safetyViolation.${guid}.componentType`, componentType),
      state,
    )
  );
};

const createOrUpdateSafetyViolationEntityByEntityTypeSuccess = (state, { data, entityType }) => {
  const safetyViolation = R.path(['driver', 'safetyViolation'], state);

  const { guid, primaryObjectGuid, safetyViolationGuid } = data;

  if (R.equals(entityType, 'safetyViolation')) {
    const notes = R.pathOr({}, [guid, 'notes'], safetyViolation);
    const documents = R.pathOr({}, [guid, 'documents'], safetyViolation);
    const expanded = R.pathOr(false, [guid, 'expanded'], safetyViolation);
    const componentType = R.path([guid, 'componentType'], safetyViolation);
    const index = R.pathOr(R.length(R.values(safetyViolation)), [guid, 'index'], safetyViolation);

    return P.$set(
      `driver.safetyViolation.${guid}`,
      R.mergeRight(data, { notes, index, expanded, documents, componentType }),
      state,
    );
  }

  const correctSafetyViolationGuid = R.or(primaryObjectGuid, safetyViolationGuid);
  const path = ['driver', 'safetyViolation', correctSafetyViolationGuid, `${entityType}s`];
  const index = R.pathOr(R.length(R.values(R.path(path, state))), [...path, guid, 'index'], state);

  return P.$set(`${R.join('.', path)}.${guid}`, R.assoc('index', index, data), state);
};

const removeSafetyViolationEntityByEntityTypeSuccess = (state, data) => {
  const { guid, entityType, primaryObjectGuid } = data;

  const path = G.ifElse(
    R.equals(entityType, 'safetyViolation'),
    `driver.safetyViolation.${guid}`,
    `driver.safetyViolation.${primaryObjectGuid}.${entityType}s.${guid}`,
  );

  return P.$drop(path, state);
};

// assignment
const getDriverAssignmentSuccess = (state, data) => P.$set('assignment', data, state);

const getAvailableAssignableSuccess = (state, { options, groupName }) => (
  P.$set(`availableAssignable.${groupName}`, options, state)
);

// monthly payroll deduction
const getDriverPayrollChargesIsPaidOnPayrollSuccess = (state, data) => (
  P.$set(
    'driver.monthlyPayrollDeductionList',
    R.map(
      (entity) => R.assoc('deductionDisabled', R.prop(G.getGuidFromObject(entity), data), entity),
      R.path(['driver', 'monthlyPayrollDeductionList'], state),
    ),
    state,
  )
);

// payroll accessorial
const getDriverPayrollAccessorialsIsPaidOnPayrollSuccess = (state, data) => (
  P.$set(
    'driver.payrollAccessorialList',
    R.map(
      (entity) => R.assoc('deductionDisabled', R.prop(G.getGuidFromObject(entity), data), entity),
      R.path(['driver', 'payrollAccessorialList'], state),
    ),
    state,
  )
);

// advance payment
const updateAdvancePaymentStatusSuccess = (state, data) => {
  const { driver: { advancePaymentList } } = state;

  const { status, integrationType } = data;

  const isRelayCancelled = R.and(
    R.equals(status, GC.ADVANCE_PAYMENT_STATUS_CANCELLED),
    R.equals(integrationType, GC.ADVANCE_PAYMENT_INTEGRATION_TYPE_RELAY),
  );

  const index = R.findIndex(R.propEq(G.getGuidFromObject(data), GC.FIELD_GUID), advancePaymentList);

  const dataToSet = isRelayCancelled ? R.assoc('disabled', true, data) : data;

  return P.$set(`driver.advancePaymentList.${index}`, dataToSet, state);
};

// filters
const setUnavailablePeriodDatesFilter = (state, filter) => (
  P.$all(
    P.$set('driver.unavailablePeriodList', null),
    P.$set('driver.totalCount.unavailablePeriodList', null),
    P.$set('driver.pagination.unavailablePeriodList.offset', 0),
    P.$set('driver.formGroupFilters.unavailablePeriodDatesFilter', filter),
    state,
  )
);

const setUnavailablePeriodReasonFilter = (state, filter) => (
  P.$all(
    P.$set('driver.unavailablePeriodList', null),
    P.$set('driver.totalCount.unavailablePeriodList', null),
    P.$set('driver.pagination.unavailablePeriodList.offset', 0),
    P.$set('driver.formGroupFilters.unavailablePeriodReasonFilter', filter),
    state,
  )
);

// expenses
const getDriverExpenseTypesSuccess = (state, data) => (
  P.$set('driverExpenseTypes', data, state)
);

const deleteDriverExpenseDocumentSuccess = (state, guid) => {
  const index = R.findIndex(
    R.propEq(guid, GC.FIELD_GUID),
    P.$get('driver.driverExpenseList', state),
  );

  return P.$all(
    P.$set(`driver.driverExpenseList.${index}.fileUri`, null),
    P.$set(`driver.driverExpenseList.${index}.fileName`, null),
    state,
  );
};

// assignment history
const toggleResetAssignmentHistoryReportByAssignmentType = (state, assignmentType) => {
  return P.$toggle(`driver.resetAssignmentHistory.${assignmentType}`, state);
};

export const driverReducer = {
  // safety-violation
  [A.toggleSafetyViolationItem]: toggleSafetyViolationItem,
  [A.getSafetyViolationListByEntityTypeSuccess]: getSafetyViolationListByEntityTypeSuccess,
  [A.removeSafetyViolationEntityByEntityTypeSuccess]: removeSafetyViolationEntityByEntityTypeSuccess,
  [A.createOrUpdateSafetyViolationEntityByEntityTypeSuccess]: createOrUpdateSafetyViolationEntityByEntityTypeSuccess,
  // assignment
  [A.getDriverAssignmentSuccess]: getDriverAssignmentSuccess,
  [A.getAvailableAssignableSuccess]: getAvailableAssignableSuccess,
  [A.toggleResetAssignmentHistoryReportByAssignmentType]: toggleResetAssignmentHistoryReportByAssignmentType,
  // monthly payroll deduction
  [A.getDriverPayrollChargesIsPaidOnPayrollSuccess]: getDriverPayrollChargesIsPaidOnPayrollSuccess,
  // payroll accessorial
  [A.getDriverPayrollAccessorialsIsPaidOnPayrollSuccess]: getDriverPayrollAccessorialsIsPaidOnPayrollSuccess,
  // advance payment
  [A.updateAdvancePaymentStatusSuccess]: updateAdvancePaymentStatusSuccess,
  // filters
  [A.setUnavailablePeriodDatesFilter]: setUnavailablePeriodDatesFilter,
  [A.setUnavailablePeriodReasonFilter]: setUnavailablePeriodReasonFilter,
  // expenses
  [A.getDriverExpenseTypesSuccess]: getDriverExpenseTypesSuccess,
  [A.deleteDriverExpenseDocumentSuccess]: deleteDriverExpenseDocumentSuccess,
};
